import React, { useState } from "react";
import MobilePage, { PageHeader } from "../../../Components/MobilePage";
import { Trans } from "react-i18next";

import MDContainer from "@mui/material/Container";
import { CardContent } from "@mui/material";
import Card from "@mui/material/Card";
import { usePictureSets } from "../../../hooks/usePictureSets";

import styled from "styled-components";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CardActions from "@mui/material/CardActions";
import makeStyles from "@mui/styles/makeStyles";
import { Link } from "react-router-dom";
import EpmsImage from "../../../../components/Common/EpmsImage";
import AppBar from "../../Components/AppBar";
import useRoutes from "../../../hooks/useRoutes";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import { generatePath } from "react-router";
import { alpha } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import useCopyrightDialog from "@/react/Webshop/hooks/useCopyrightDialog";

const Year = styled.div`
  font-size: 16px;
`;

const SubjectName = styled.h4`
  font-size: 20px;
  font-weight: 500;
  margin: 0;
`;

const JobName = styled.h5`
  line-height: 24px;
  font-size: 16px;
  margin: 0;
  font-weight: 400;
`;

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: "8px",
    position: "relative",
  },
  pictureLContent: {},
  pictureContainerWrapper: {
    paddingTop: "100%",
    width: "100%",
    position: "relative",
  },
  pictureContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& > img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      backgroundColor: "white",
    },
  },
  listItem: {
    position: "relative",
  },

  morePicturesContainer: {
    position: "absolute",
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: alpha(theme.palette.primary.main, 0.8),
    color: theme.palette.primary.contrastText,
    margin: theme.spacing(1),
  },
  actionsContainer: {
    justifyContent: "space-between",
  },
}));

const MyPictureSets = () => {
  const { isLoaded, pictureSets } = usePictureSets();
  const { getCopyrightTriggerProps, copyrightDialog } = useCopyrightDialog();

  if (!isLoaded) {
    return <div>Loading</div>;
  }

  return (
    <MobilePage>
      <AppBar />
      <PageHeader>
        <Trans>Picture sets</Trans>
      </PageHeader>
      <MDContainer>
        {pictureSets.map((pictureSet) => {
          return (
            <PictureSetInList
              key={pictureSet.subject.uuid}
              pictureSet={pictureSet}
              pictureContextMenuProps={getCopyrightTriggerProps()}
            />
          );
        })}
      </MDContainer>
      {copyrightDialog}
    </MobilePage>
  );
};

const PictureSetInList = ({ pictureSet, pictureContextMenuProps }) => {
  const classes = useStyles();
  const routes = useRoutes();
  const [anchorEl, setAnchorEl] = useState(null);

  const boughtAtLeastOnePicture =
    pictureSet.digitalSet.IP.bought ||
    pictureSet.digitalSet.SFP.bought ||
    pictureSet.digitalSet.GP.bought ||
    pictureSet.pictures.some((picture) => picture.bought);

  const createdDate = new Date(0);
  createdDate.setUTCSeconds(pictureSet.job.creationTime);
  const year = createdDate.getFullYear();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const numNotDisplayedPictures = pictureSet.pictures.length - 6;

  return (
    <Card className={classes.card} key={pictureSet.subject.uuid}>
      <CardContent className={classes.cardContent}>
        <Box display="flex" justifyContent="space-between">
          <Box>
            <Year>{year}</Year>
            <SubjectName>{pictureSet.subject.name}</SubjectName>
            <JobName>{pictureSet.job.name}</JobName>
          </Box>
          {pictureSet.isEditable && (
            <Box>
              <IconButton
                onClick={handleClick}
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                }}
              >
                <MoreVertIcon />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                keepMounted
                open={open}
                onClose={handleClose}
              >
                {pictureSet.qualifyForSelectOwnFavorites && (
                  <MenuItem
                    onClick={handleClose}
                    component={Link}
                    to={generatePath(
                      routes["myaccount.picturesets.pictureset.favorites"]
                        .path,
                      { uuid: pictureSet.subject.uuid },
                    )}
                  >
                    <Trans>Select favorites</Trans>
                  </MenuItem>
                )}
                {pictureSet.job.yearbookPoseSelection && (
                  <MenuItem
                    onClick={handleClose}
                    component={Link}
                    to={generatePath(
                      routes["myaccount.picturesets.pictureset.yearbookpose"]
                        .path,
                      { uuid: pictureSet.subject.uuid },
                    )}
                  >
                    <Trans>Choose yearbook pose</Trans>
                  </MenuItem>
                )}
              </Menu>
            </Box>
          )}
        </Box>
        <Grid container className={classes.root} spacing={1}>
          {pictureSet.pictures.slice(0, 6).map((picture, index) => {
            console.log(
              "Rendering the first pictures of",
              pictureSet.pictures,
            );
            return (
              <Grid
                key={picture.uuid}
                size={{ xs: 4 }}
                className={classes.listItem}
              >
                <Box className={classes.pictureContainerWrapper}>
                  <Box className={classes.pictureContainer}>
                    <EpmsImage
                      uuid={picture.uuid}
                      {...(pictureContextMenuProps ?? {})}
                    />
                    {index === 5 && numNotDisplayedPictures > 0 && (
                      <div className={classes.morePicturesContainer}>
                        {`+${numNotDisplayedPictures}`}
                      </div>
                    )}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
      <CardActions classes={{ root: classes.actionsContainer }}>
        <Button
          size="small"
          component={Link}
          color="primary"
          to={`/my-account/picture-sets/${pictureSet.subject.uuid}`}
        >
          {boughtAtLeastOnePicture ? (
            <Trans>View and download</Trans>
          ) : (
            <Trans>View</Trans>
          )}
        </Button>
        <Button
          size="small"
          component={Link}
          color="primary"
          to={routes["products.list"].path}
        >
          <Trans>Buy products</Trans>
        </Button>
      </CardActions>
    </Card>
  );
};

export default MyPictureSets;
